<template>
  <el-drawer
    title="Account Details"
    :visible.sync="showDrawer"
    :with-header="true"
    :before-close="handleClose"
    :size="drawerWidth"
    :wrapperClosable="false"
  >
    <section v-if="!isAssetAccount" class="px-5">
      <div class="grid grid-cols-12 mb-5 border-b pb-5">
        <p class="col-span-6 text-13px text-fg-muted font-normal">Currency:</p>
        <div
          class="col-span-5 text-13px font-normal text-black flex items-center gap-x-2"
        >
          <currency-icons :currency="getCurrencyCountry(selectedCurrency)" />
          <p>{{ selectedCurrency }}</p>
        </div>
      </div>
      <div v-if="isGBPAccount">
        <div class="grid grid-cols-12 mb-5 border-b pb-5">
          <p class="col-span-6 text-13px text-fg-muted font-normal">
            Account Number:
          </p>
          <div
            class="col-span-5 text-13px font-normal text-black flex items-center gap-x-2"
          >
            <p>
              {{ accountNumber }}
            </p>
          </div>
          <div
            class="col-span-1 cursor-pointer"
            v-clipboard:copy="accountNumber"
            v-clipboard:success="onCopy"
            v-clipboard:error="onError"
          >
            <copy-icon />
          </div>
        </div>
        <div class="grid grid-cols-12 mb-5 border-b pb-5">
          <p class="col-span-6 text-13px text-fg-muted font-normal">Iban:</p>
          <div
            class="col-span-5 text-13px font-normal text-black flex items-center gap-x-2"
          >
            <p>
              {{ IBAN }}
            </p>
          </div>
          <div
            class="col-span-1 cursor-pointer"
            v-clipboard:copy="IBAN"
            v-clipboard:success="onCopy"
            v-clipboard:error="onError"
          >
            <copy-icon />
          </div>
        </div>
        <div class="grid grid-cols-12 mb-5 border-b pb-5">
          <p class="col-span-6 text-13px text-fg-muted font-normal">
            Sort code:
          </p>
          <div
            class="col-span-5 text-13px font-normal text-black flex items-center gap-x-2"
          >
            <p>
              {{ sortCode }}
            </p>
          </div>
          <div
            class="col-span-1 cursor-pointer"
            v-clipboard:copy="sortCode"
            v-clipboard:success="onCopy"
            v-clipboard:error="onError"
          >
            <copy-icon />
          </div>
        </div>
        <div class="grid grid-cols-12 mb-5 border-b pb-5">
          <p class="col-span-6 text-13px text-fg-muted font-normal">
            Reference:
          </p>
          <div
            class="col-span-5 text-13px font-normal text-black flex items-center gap-x-2"
          >
            <p>
              {{
                selectedAccountDetails.transferInstructions.reference || '--'
              }}
            </p>
          </div>
        </div>
      </div>
      <div v-if="isNonNGNLocalCurrencyAccount">
        <!-- BE is returning a different structure for GHS and XAF, thats why we have this section -->
        <div class="grid grid-cols-12 mb-5 border-b pb-5">
          <p class="col-span-6 text-13px text-fg-muted font-normal">
            Account Name:
          </p>
          <div
            class="col-span-5 text-13px font-normal text-black flex items-center gap-x-2"
          >
            <p>
              {{
                selectedAccountDetails.transferInstructions.accountName || '--'
              }}
            </p>
          </div>
        </div>
        <div class="grid grid-cols-12 mb-5 border-b pb-5">
          <p class="col-span-6 text-13px text-fg-muted font-normal">
            Account Number:
          </p>
          <div
            class="col-span-5 text-13px font-normal text-black flex items-center gap-x-2"
          >
            <p>
              {{
                selectedAccountDetails.transferInstructions.accountNumber ||
                '--'
              }}
            </p>
          </div>
          <div
            class="col-span-1 cursor-pointer"
            v-clipboard:copy="
              selectedAccountDetails.transferInstructions.accountNumber
            "
            v-clipboard:success="onCopy"
            v-clipboard:error="onError"
          >
            <copy-icon />
          </div>
        </div>
        <div class="grid grid-cols-12 mb-5 border-b pb-5">
          <p class="col-span-6 text-13px text-fg-muted font-normal">
            Bank Name:
          </p>
          <div
            class="col-span-5 text-13px font-normal text-black flex items-center gap-x-2"
          >
            <p>
              {{ selectedAccountDetails.transferInstructions.bankName || '--' }}
            </p>
          </div>
        </div>
        <div class="grid grid-cols-12 mb-5 border-b pb-5">
          <p class="col-span-6 text-13px text-fg-muted font-normal">
            Bank Address:
          </p>
          <div
            class="col-span-5 text-13px font-normal text-black flex items-center gap-x-2"
          >
            <p>
              {{
                selectedAccountDetails.transferInstructions.bankAddress || '--'
              }}
            </p>
          </div>
        </div>
      </div>
      <div v-if="isNonGBPFiat">
        <div class="grid grid-cols-12 mb-5 border-b pb-5">
          <p class="col-span-6 text-13px text-fg-muted font-normal">
            Account Name:
          </p>
          <div
            class="col-span-5 text-13px font-normal text-black flex items-center gap-x-2"
          >
            <p>
              {{ accountName }}
            </p>
          </div>
        </div>
        <div class="grid grid-cols-12 mb-5 border-b pb-5">
          <p class="col-span-6 text-13px text-fg-muted font-normal">
            Account Number:
          </p>
          <div
            class="col-span-5 text-13px font-normal text-black flex items-center gap-x-2"
          >
            <p>
              {{ accountNumber }}
            </p>
          </div>
          <div
            class="col-span-1 cursor-pointer"
            v-clipboard:copy="accountNumber"
            v-clipboard:success="onCopy"
            v-clipboard:error="onError"
          >
            <copy-icon />
          </div>
        </div>
        <div class="grid grid-cols-12 mb-5 border-b pb-5">
          <p class="col-span-6 text-13px text-fg-muted font-normal">
            Bank Name:
          </p>
          <div
            class="col-span-5 text-13px font-normal text-black flex items-center gap-x-2"
          >
            <p>
              {{ bankName }}
            </p>
          </div>
        </div>
        <div v-if="selectedCurrency === $CURRENCY.USD">
          <div class="grid grid-cols-12 mb-5 border-b pb-5">
            <p class="col-span-6 text-13px text-fg-muted font-normal">
              Routing Number:
            </p>
            <div
              class="col-span-5 text-13px font-normal text-black flex items-center gap-x-2"
            >
              <p>{{ routingNumber }}</p>
            </div>
            <div
              class="col-span-1 cursor-pointer"
              v-clipboard:copy="routingNumber"
              v-clipboard:success="onCopy"
              v-clipboard:error="onError"
            >
              <copy-icon />
            </div>
          </div>
          <div class="grid grid-cols-12 mb-5 border-b pb-5">
            <p class="col-span-6 text-13px text-fg-muted font-normal">
              Swift Code:
            </p>
            <div
              class="col-span-5 text-13px font-normal text-black flex items-center gap-x-2"
            >
              <p>{{ swiftCode }}</p>
            </div>
          </div>
        </div>
        <div class="grid grid-cols-12 mb-5 border-b pb-5">
          <p class="col-span-6 text-13px text-fg-muted font-normal">
            Reference:
          </p>
          <div
            class="col-span-5 text-13px font-normal text-black flex items-center gap-x-2"
          >
            <p>{{ reference }}</p>
          </div>
          <div
            class="col-span-1 cursor-pointer"
            v-clipboard:copy="reference"
            v-clipboard:success="onCopy"
            v-clipboard:error="onError"
          >
            <copy-icon />
          </div>
        </div>
      </div>
    </section>
    <section v-else class="px-6 py-4 -mt-4">
      <p class="text-sm font-light">Select Network</p>
      <div class="border rounded-lg mt-2">
        <el-select
          v-model="activeAssetNetwork"
          placeholder="Select Network"
          class="w-full"
        >
          <el-option
            v-for="(network, index) in assetNetworks"
            :key="index"
            :label="network.value"
            :value="network"
            class="mt-4"
          >
            <div class="flex items-center">
              <p class="w-full text-black">{{ network.label }}</p>
              <p class="ml-2">
                <network-icons :currency="getNetworkFullName(network.value)" />
              </p>
              <p
                v-if="activeAssetNetwork.value === network.value"
                class="ml-16"
              >
                <small-check />
              </p>
            </div>
          </el-option>
        </el-select>
      </div>
      <div
        class="mt-4 border px-8 py-8 rounded-lg flex flex-col items-center justify-center"
      >
        <div
          class="col-span-5 text-13px font-normal text-black flex items-center gap-x-4 mb-10"
        >
          <currency-icons
            :currency="getCurrencyCountry(selectedCurrency)"
            size="h-8 w-8"
          />
          <p class="flex flex-col">
            <span class="text-xs text-gray-muted">Asset</span>
            <span>{{ selectedCurrency }}</span>
          </p>
        </div>
        <p class="text-gray-muted font-medium mb-10">
          Scan address to make deposit
        </p>

        <qrcode-vue
          :value="selectedAssetAccountDetails.walletAddress"
          :size="250"
          level="H"
          class="mb-5"
        />
        <div
          class="bg-bg-muted w-full flex justify-center flex-col items-center mt-5 py-3 rounded-lg my-5"
        >
          <p class="text-xs text-fg-muted font-normal">DEPOSIT ADDRESS</p>
          <div class="flex items-center mt-3">
            <p class="text-xs text-black font-medium mr-0.5">
              {{ maskID(selectedAssetAccountDetails.walletAddress) }}
            </p>
            <div
              class="cursor-pointer"
              v-clipboard:copy="selectedAssetAccountDetails.walletAddress"
              v-clipboard:success="onCopy"
              v-clipboard:error="onError"
            >
              <copy-icon />
            </div>
          </div>
        </div>
        <div class="flex items-center justify-center mt-5">
          <div
            class="flex items-center justify-center gap-x-4 px-3 py-2 rounded-md shadow-sm border border-red-2 bg-pink-1"
          >
            <warning-icon-2 size="h-6 w-6" color="#FF0000" />
            <p class="text-red-1 text-sm font-normal">
              Only send {{ activeAssetNetwork.label }} to this address
            </p>
          </div>
        </div>
      </div>
    </section>
  </el-drawer>
</template>
<script>
// Todo: refactor implementation to not use repeated functions and logic in the deposit account cards
import breakpoints from '@/utils/breakpoints.js'
import {
  ASSETS,
  FIAT,
  NON_NGN_LOCAL_CURRENCIES,
  ASSETS_NETWORKS
} from '@/utils/constants'
import {
  GetCurrencyCountry,
  GetNetworkFullName,
  MaskID,
  ParseNetworkName
} from '@/utils/helpers.js'
import QrcodeVue from 'qrcode.vue'
export default {
  name: 'Drawer',
  data () {
    return {
      breakpoints,
      assets: ASSETS,
      fiat: FIAT,
      nonNGNLocalCurrencies: NON_NGN_LOCAL_CURRENCIES,
      activeAssetNetwork: { value: '' }
    }
  },
  props: {
    showDrawer: {
      type: Boolean,
      required: true
    }
  },
  components: {
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    CopyIcon: () => import('@/assets/icons/CopyIcon.vue'),
    NetworkIcons: () => import('@/assets/v2/icons/NetworkIcons.vue'),
    SmallCheck: () => import('@/assets/v2/icons/SmallCheck.vue'),
    WarningIcon2: () => import('@/assets/icons/WarningIcon2.vue'),
    QrcodeVue
  },
  methods: {
    parseNetworkName (networkType) {
      return ParseNetworkName(networkType)
    },
    maskID (address) {
      return MaskID(address)
    },
    getNetworkFullName (val) {
      return GetNetworkFullName(val)
    },
    setDefaultAssetWallet () {
      if (this.isAssetAccount) {
        this.activeAssetNetwork.value = this.selectedAccountDetails[0].network
      }
    },
    onError: function (e) {
      this.$notify.error('Error copying to clipboard!')
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    onCopy: function (e) {
      this.$notify.success({
        title: 'Copied'
      })
    },
    handleClose () {
      this.$emit('closeDrawer')
    }
  },
  computed: {
    assetNetworks () {
      return this.isAssetAccount
        ? ASSETS_NETWORKS.filter((assetNetwork) =>
            this.selectedAccountDetails.some(
              (item) => item.network === assetNetwork.value
            )
          )
        : []
    },
    isAssetAccount () {
      const asset = this.assets.find(
        (item) => item.currency === this.selectedCurrency
      )
      return !!asset
    },
    accountName () {
      return (
        // BCB don't have account name
        this.bankName === 'BCB Group'
          ? this.business.name
          : this.selectedAccountDetails?.transferInstructions?.account_name ||
              this.selectedAccountDetails?.transferInstructions?.wireInstruction
                ?.creditTo ||
              this.selectedAccountDetails?.transferInstructions?.accountName ||
              '--'
      )
    },
    IBAN () {
      return (
        this.selectedAccountDetails?.transferInstructions?.iban ||
        this.selectedAccountDetails?.transferInstructions?.wireInstruction
          ?.iban ||
        this.selectedAccountDetails?.transferInstructions?.iban ||
        '--'
      )
    },
    sortCode () {
      return (
        this.selectedAccountDetails?.transferInstructions?.sort_code ||
        this.selectedAccountDetails?.transferInstructions?.wireInstruction
          ?.sortCode ||
        this.selectedAccountDetails?.transferInstructions?.sortCode ||
        '--'
      )
    },
    accountNumber () {
      return (
        this.selectedAccountDetails?.transferInstructions?.account_number ||
        this.selectedAccountDetails?.transferInstructions?.wireInstruction
          ?.accountNumber ||
        this.selectedAccountDetails?.transferInstructions?.accountNumber ||
        '--'
      )
    },
    bankName () {
      return (
        this.selectedAccountDetails?.transferInstructions?.bank_name ||
        this.selectedAccountDetails?.transferInstructions?.wireInstruction
          ?.depositoryBankName ||
        this.selectedAccountDetails?.transferInstructions?.bankName ||
        '--'
      )
    },
    routingNumber () {
      return (
        this.selectedAccountDetails?.transferInstructions?.rounting_number ||
        this.selectedAccountDetails?.transferInstructions?.wireInstruction
          ?.routingNumber ||
        this.selectedAccountDetails?.transferInstructions?.routingNumber ||
        '--'
      )
    },
    swiftCode () {
      return (
        this.selectedAccountDetails?.transferInstructions?.swift_code ||
        this.selectedAccountDetails?.transferInstructions?.wireInstruction
          ?.swiftCode ||
        this.selectedAccountDetails?.transferInstructions?.swiftCode ||
        '--'
      )
    },
    reference () {
      return (
        this.selectedAccountDetails?.transferInstructions?.reference ||
        this.selectedAccountDetails?.transferInstructions?.wireInstruction
          ?.reference ||
        this.selectedAccountDetails?.transferInstructions?.reference ||
        '--'
      )
    },
    drawerWidth () {
      return breakpoints.lgAndUp ? '33%' : breakpoints.mdAndUp ? '70%' : '90%'
    },
    selectedCurrency () {
      return this.$store.state.report.accountDetailsCurrency
    },
    isGBPAccount () {
      return this.selectedCurrency === this.$CURRENCY.GBP
    },
    isNonGBPFiat () {
      const fiat = this.fiat.find(
        (item) =>
          item.currency === this.selectedCurrency &&
          (item.currency !== this.$CURRENCY.GBP) &
            !this.nonNGNLocalCurrencies.includes(item.currency)
      )
      return fiat
    },
    isNonNGNLocalCurrencyAccount () {
      return this.nonNGNLocalCurrencies.includes(this.selectedCurrency)
    },
    currentAccountDetails () {
      return this.$store.state.transactions.currentAccountInfo.items || []
    },
    selectedAccountDetails () {
      if (!this.isAssetAccount) {
        const item = this.currentAccountDetails.find(
          (item) =>
            item.currency === this.selectedCurrency ||
            item.asset === this.selectedCurrency
        )
        return Object.keys(item).length > 0
          ? item
          : { walletAddress: '', network: '' }
      } else {
        const item = this.currentAccountDetails.filter(
          (item) =>
            item.currency === this.selectedCurrency ||
            item.asset === this.selectedCurrency
        )
        return item
      }
    },
    selectedAssetAccountDetails () {
      return this.isAssetAccount
        ? this.selectedAccountDetails.find(
            (asset) => asset.network === this.activeAssetNetwork.value
          )
        : []
    },
    business () {
      return this.$store.state.auth.business
    }
  },
  mounted () {
    this.setDefaultAssetWallet()
  }
}
</script>
<style scoped>
>>> .el-drawer__header > :first-child {
  font-size: 0.9rem;
  color: var(--text-2);
  font-weight: 600;
}
>>> .el-drawer__header {
  color: var(--text-4);
  font-size: 1.2rem;
  padding-bottom: 1rem;
  padding-top: 2rem;
  padding-left: 1.4rem;
  font-weight: 700;
  border-bottom: 1px solid var(--border-2);
}
>>> .el-drawer {
  background: var(--background-9) !important;
}
</style>
